import { JwtPayload, RequestGuestAccountInputDto, Roles, TokenResultBody } from '@llx/common';
import { jwtDecode } from 'jwt-decode';
import Keycloak from 'keycloak-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { axiosClient } from '@/axiosClient';
import { config } from '@/environment';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import {
  clearPersistedTokens,
  createKeycloakInstance,
  loadPersistedTokens,
  persistTokens,
  persistTokensRaw,
} from './keycloak';

// Prevent multiple simultaneous refresh calls
let globalRefreshPromise: Promise<string | null> | null = null;

// ─── Context type ───────────────────────────────────────────────

type BaseContextType = {
  getFreshToken: () => Promise<string | null>;
  askForGuestAccess: (args: RequestGuestAccountInputDto) => Promise<void>;
  isRequestingGuestAccount: boolean;
  login: (returnUrl?: string) => void;
  logout: () => void;
};

type InvalidTokenContextType = BaseContextType & {
  tokenStatus: 'invalid';
};

type LoadingTokenContextType = BaseContextType & {
  tokenStatus: 'loading';
};

type ValidTokenContextType = BaseContextType & {
  tokenStatus: 'valid';
  isAdmin: boolean;
  userID: string;
  email: string;
};

export type KeycloakAuthContextType =
  | InvalidTokenContextType
  | LoadingTokenContextType
  | ValidTokenContextType;

export const KeycloakAuthContext = createContext<KeycloakAuthContextType | undefined>(undefined);

// ─── Provider ───────────────────────────────────────────────────

type Props = {
  children: React.ReactNode;
};

export function KeycloakProvider({ children }: Props) {
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoggingInAsGuest, setIsLoggingInAsGuest] = useState(false);

  const [decodedToken, setDecodedToken] = useState<JwtPayload | null>(null);

  const keycloakRef = useRef<Keycloak | null>(null);

  // ─── helpers ────────────────────────────────────────────────

  const decodeAndStore = useCallback((kc: Keycloak) => {
    if (!kc.token) {
      console.log('[KC] decodeAndStore: no token on instance');
      setDecodedToken(null);
      return;
    }
    try {
      const decoded = jwtDecode<JwtPayload>(kc.token);
      console.log('[KC] decodeAndStore: decoded sub=%s email=%s', decoded.sub, decoded.email);
      setDecodedToken(decoded);
    } catch (err) {
      console.error('[KC] decodeAndStore: JWT decode failed', err);
      captureSentry({ title: 'Failed to decode keycloak token', error: err });
      setDecodedToken(null);
    }
  }, []);

  const setupEventListeners = useCallback(
    (kc: Keycloak) => {
      kc.onAuthSuccess = () => {
        console.log(
          '[KC] EVENT onAuthSuccess – authenticated=%s token=%s',
          kc.authenticated,
          !!kc.token
        );
        persistTokens(kc);
        decodeAndStore(kc);
        setAuthenticated(true);
      };

      kc.onAuthRefreshSuccess = () => {
        console.log('[KC] EVENT onAuthRefreshSuccess');
        persistTokens(kc);
        decodeAndStore(kc);
        setAuthenticated(true);
      };

      kc.onTokenExpired = () => {
        console.log('[KC] EVENT onTokenExpired');
        kc.updateToken(30).catch(() => {
          clearPersistedTokens();
          setAuthenticated(false);
          setDecodedToken(null);
        });
      };

      kc.onAuthLogout = () => {
        console.log('[KC] EVENT onAuthLogout');
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
      };

      kc.onAuthError = (errorData) => {
        console.error('[KC] EVENT onAuthError', errorData);
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
      };
    },
    [decodeAndStore]
  );

  // ─── init ─────────────────────────────────────────────────

  useEffect(() => {
    const kc = createKeycloakInstance();
    keycloakRef.current = kc;
    setupEventListeners(kc);

    const stored = loadPersistedTokens();

    console.log('[KC] init starting – URL: %s', window.location.href);
    console.log(
      '[KC] init – stored tokens: token=%s refresh=%s id=%s',
      !!stored.token,
      !!stored.refreshToken,
      !!stored.idToken
    );

    const hasStoredTokens = !!(stored.token && stored.refreshToken);

    kc.init({
      ...(hasStoredTokens && {
        token: stored.token,
        refreshToken: stored.refreshToken,
        idToken: stored.idToken,
      }),
      pkceMethod: 'S256',
      checkLoginIframe: false,
      scope: 'openid email phone offline_access',
    })
      .then((auth) => {
        console.log(
          '[KC] init resolved – auth=%s kc.authenticated=%s kc.token=%s',
          auth,
          kc.authenticated,
          !!kc.token
        );

        if (auth) {
          persistTokens(kc);
          decodeAndStore(kc);
          setAuthenticated(true);
          setInitialized(true);
          return;
        }

        // keycloak-js v26 sometimes rejects valid stored tokens.
        // Try a direct token refresh – fast (~100ms), no new instance needed.
        if (hasStoredTokens && stored.refreshToken) {
          console.log('[KC] stored-token init returned auth=false, trying token refresh');
          kc.refreshToken = stored.refreshToken;
          return kc
            .updateToken(-1)
            .then(() => {
              persistTokens(kc);
              decodeAndStore(kc);
              setAuthenticated(true);
              setInitialized(true);
            })
            .catch(() => {
              console.log('[KC] token refresh also failed, clearing tokens');
              clearPersistedTokens();
              setAuthenticated(false);
              setInitialized(true);
            });
        }

        setAuthenticated(false);
        setInitialized(true);
      })
      .catch((err) => {
        console.error('[KC] init FAILED', err);
        captureSentry({ title: 'Keycloak init failed', error: err });
        clearPersistedTokens();
        setAuthenticated(false);
        setInitialized(true);
      });

    return () => {
      kc.onAuthSuccess = undefined;
      kc.onAuthRefreshSuccess = undefined;
      kc.onTokenExpired = undefined;
      kc.onAuthLogout = undefined;
      kc.onAuthError = undefined;
    };
  }, []);

  // ─── getFreshToken ─────────────────────────────────────────

  const getFreshToken = useCallback(async (): Promise<string | null> => {
    const kc = keycloakRef.current;
    if (!kc?.token) return null;

    if (globalRefreshPromise) {
      try {
        return await globalRefreshPromise;
      } catch {
        return null;
      }
    }

    globalRefreshPromise = (async () => {
      try {
        await kc.updateToken(30);
        persistTokens(kc);
        return kc.token ?? null;
      } catch {
        clearPersistedTokens();
        setAuthenticated(false);
        setDecodedToken(null);
        return null;
      } finally {
        globalRefreshPromise = null;
      }
    })();

    return globalRefreshPromise;
  }, []);

  // ─── login ────────────────────────────────────────────────

  const login = useCallback((returnUrl?: string) => {
    const kc = keycloakRef.current;
    if (!kc) return;

    const redirectUri = returnUrl
      ? `${window.location.origin}${returnUrl}`
      : window.location.origin;

    void kc.login({
      redirectUri,
      scope: 'openid email phone offline_access',
    });
  }, []);

  // ─── logout ───────────────────────────────────────────────

  const logout = useCallback(() => {
    const kc = keycloakRef.current;

    clearPersistedTokens();

    if (kc?.authenticated) {
      void kc.logout({ redirectUri: window.location.origin });
    } else {
      setAuthenticated(false);
      setDecodedToken(null);
      window.location.href = '/';
    }
  }, []);

  // ─── loginAsGuest ─────────────────────────────────────────

  const loginAsGuest = useCallback(
    async (args: RequestGuestAccountInputDto): Promise<void> => {
      setIsLoggingInAsGuest(true);
      try {
        const response = await axiosClient.post<TokenResultBody>(
          `${config.BACKEND_URL}/auth/guest-account`,
          args,
          { timeout: 15000 }
        );

        const { access_token, refresh_token, id_token } = response.data;

        persistTokensRaw(access_token, refresh_token, id_token);

        // Tokens are issued with azp=frontend (matching keycloak-js client),
        // so keycloak-js can handle refresh natively
        const newKc = createKeycloakInstance();
        setupEventListeners(newKc);
        keycloakRef.current = newKc;

        const auth = await newKc.init({
          token: access_token,
          refreshToken: refresh_token,
          idToken: id_token,
          checkLoginIframe: false,
        });

        if (auth) {
          persistTokens(newKc);
          decodeAndStore(newKc);
          setAuthenticated(true);
        } else {
          clearPersistedTokens();
          setAuthenticated(false);
          throw new Error('Guest login: keycloak-js rejected tokens');
        }
      } catch (err) {
        captureSentry({ title: 'Guest login failed', error: err });
        throw err;
      } finally {
        setIsLoggingInAsGuest(false);
      }
    },
    [setupEventListeners, decodeAndStore]
  );

  // ─── context value ────────────────────────────────────────

  const contextValue: KeycloakAuthContextType = useMemo(() => {
    const base: BaseContextType = {
      getFreshToken,
      askForGuestAccess: loginAsGuest,
      isRequestingGuestAccount: isLoggingInAsGuest,
      login,
      logout,
    };

    if (!initialized) {
      console.log('[KC] contextValue: tokenStatus=loading (not initialized)');
      return { ...base, tokenStatus: 'loading' } satisfies LoadingTokenContextType;
    }

    if (!authenticated || !decodedToken) {
      console.log(
        '[KC] contextValue: tokenStatus=invalid (authenticated=%s, decodedToken=%s)',
        authenticated,
        !!decodedToken
      );
      return { ...base, tokenStatus: 'invalid' } satisfies InvalidTokenContextType;
    }

    const isAdmin = decodedToken.roles?.includes(Roles.admin) ?? false;
    const userID = decodedToken.sub ?? '';
    const email = decodedToken.email ?? '';

    if (!userID || !email) {
      console.log(
        '[KC] contextValue: tokenStatus=invalid (no userID=%s or email=%s)',
        userID,
        email
      );
      return { ...base, tokenStatus: 'invalid' } satisfies InvalidTokenContextType;
    }

    console.log('[KC] contextValue: tokenStatus=valid userID=%s email=%s', userID, email);
    return {
      ...base,
      tokenStatus: 'valid',
      isAdmin,
      userID,
      email,
    } satisfies ValidTokenContextType;
  }, [
    initialized,
    authenticated,
    decodedToken,
    getFreshToken,
    loginAsGuest,
    isLoggingInAsGuest,
    login,
    logout,
  ]);

  return (
    <KeycloakAuthContext.Provider value={contextValue}>{children}</KeycloakAuthContext.Provider>
  );
}

export const useKeycloakAuth = () => {
  const context = useContext(KeycloakAuthContext);
  if (context === undefined) {
    throw new Error('useKeycloakAuth must be used within a KeycloakProvider');
  }
  return context;
};
